// Logique pure du moteur de formulaire (aucune dépendance au DOM ni à l'API),
// testée à part. Le contrat avec l'API est inchangé : mêmes réponses
// `{ question, value }`, même DSL de conditions (`q1==1`, `q6in3`, `&&`),
// mêmes charges utiles d'envoi.

const MULTI_VALUE_TYPES = ["checkboxList", "checkboxListGroup"];

/** Nom du contrôle (« btnList », « email »…) d'une question à partir des INPUT_TYPES de l'API. */
export function questionTypeName(question, inputTypes = []) {
  const found = inputTypes.find((t) => String(t.id) === String(question.type));
  return found ? found.type : "text";
}

export function isMultiValue(question, inputTypes) {
  return MULTI_VALUE_TYPES.includes(questionTypeName(question, inputTypes));
}

export function initialAnswers(questions, inputTypes) {
  return questions.map((question) => ({
    question: question.id,
    value: isMultiValue(question, inputTypes) ? [] : "",
  }));
}

export function findAnswer(answers, questionId) {
  return answers.find((a) => String(a.question) === String(questionId));
}

/**
 * Condition d'affichage d'une question. Clauses reliées par `&&`, chacune de la
 * forme `q<id>==<valeur>` (égalité sans tenir compte du type) ou
 * `q<id>in<valeur>` (appartenance à une réponse multiple).
 */
export function isConditionFulfilled(condition, answers) {
  if (!condition) return true;
  return condition.split("&&").every((clause) => {
    const isIn = clause.includes("in");
    const [left, right] = clause.split(isIn ? "in" : "==");
    const questionId = parseInt(left.split("q")[1], 10);
    const answer = findAnswer(answers, questionId);
    if (!answer) return false;
    if (isIn) {
      return Array.isArray(answer.value) && answer.value.includes(parseInt(right, 10));
    }
    return String(answer.value) === String(right).trim();
  });
}

/**
 * Étape réellement affichable à partir d'une étape visée : les questions dont
 * la condition n'est pas remplie sont sautées dans le sens du déplacement.
 * Au-delà de la dernière question, le formulaire doit être envoyé.
 */
export function resolveStep(questions, answers, target, direction = "forward") {
  const delta = direction === "backward" ? -1 : 1;
  let step = target;
  while (step >= 0 && step < questions.length) {
    if (isConditionFulfilled(questions[step].condition, answers)) {
      return { step };
    }
    step += delta;
  }
  if (step < 0) return { step: 0 };
  return { submit: true };
}

/**
 * Applique un changement de réponse (forme d'événement produite par Control :
 * `{ name, value, formArray?, isExclusive?, override? }`) et renvoie un nouveau
 * tableau de réponses.
 */
export function applyAnswer(answers, question, target, inputTypes) {
  const next = answers.map((a) => ({ ...a, value: cloneValue(a.value) }));
  const answer = findAnswer(next, target.name);
  if (!answer) return next;
  const { value } = target;
  const typeName = questionTypeName(question, inputTypes);

  if (typeName === "checkboxListGroup" || String(question.type) === "13") {
    // Une seule réponse par groupe, plusieurs groupes possibles.
    if (target.override) {
      answer.value = value;
    } else {
      const ref = question.answers || [];
      const picked = ref.find((a) => a.value == value); // eslint-disable-line eqeqeq
      const group = picked ? picked.group : undefined;
      const groupValues = ref.filter((a) => a.group === group).map((a) => a.value);
      answer.value = answer.value.filter((v) => !groupValues.includes(v));
      answer.value.push(value);
    }
  } else if (target.formArray) {
    const ref = question.answers || [];
    const exclusives = ref.filter((a) => a.isExclusive).map((a) => a.value);
    if (target.formArray.target.checked) {
      if (target.isExclusive) {
        answer.value = [value];
      } else {
        answer.value = answer.value.filter((v) => !exclusives.includes(v));
        answer.value.push(value);
      }
    } else {
      answer.value = answer.value.filter((v) => v != value); // eslint-disable-line eqeqeq
    }
  } else {
    answer.value = value;
  }
  return next;
}

function cloneValue(value) {
  return Array.isArray(value) ? [...value] : value;
}

export function buildSubmitPayload(
  form,
  answers,
  { formatSaveData = false, additionalDataToSave = false, specialCaseRDSP = false }
) {
  let datas = answers.map((a) => ({ ...a, value: cloneValue(a.value) }));
  if (formatSaveData) {
    datas = {};
    form.questions.forEach((question) => {
      datas[question.attribute] = findAnswer(answers, question.id).value;
    });
    if (additionalDataToSave) {
      datas = { ...additionalDataToSave, ...datas };
    }
  }
  if (specialCaseRDSP) {
    datas = { rdsp: datas, ...additionalDataToSave };
  }
  return datas;
}

export function validationUrl({ formValidationUrl, formDataUrl }, step) {
  return formValidationUrl
    ? formValidationUrl + "/" + step
    : formDataUrl + "validation/" + step;
}

export function isAnswerEmpty(answer) {
  if (!answer) return true;
  const { value } = answer;
  if (Array.isArray(value) || typeof value === "string") return value.length === 0;
  return value === null || value === undefined;
}
